/**
 * Smart Media Gallery
 * Handles thumbnail click to switch main image
 * Desktop: vertical thumbnails on left
 * Mobile: horizontal carousel thumbnails below
 */
(function () {
  'use strict';

  class SmartMediaGallery {
    constructor(gallery) {
      this.gallery = gallery;
      if (!this.gallery) return;

      this.slides = Array.from(
        this.gallery.querySelectorAll('.smart-gallery__slide')
      );
      this.thumbs = Array.from(
        this.gallery.querySelectorAll('.smart-gallery__thumb')
      );
      this.shareBtn = this.gallery.querySelector('[data-smart-share]');
      this.mobileCarousel = this.gallery.querySelector(
        '[data-smart-mobile-thumbnails]'
      );
      this.mobileProgress = this.gallery.querySelector(
        '[data-smart-mobile-progress]'
      );

      this.currentIndex = 0;

      this.bindEvents();
      this.updateMobileProgress();
    }

    bindEvents() {
      // Thumbnail clicks
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', (e) => {
          const index = parseInt(thumb.dataset.index, 10);
          this.goToSlide(index);
        });
      });

      // Share button
      if (this.shareBtn) {
        this.shareBtn.addEventListener('click', () => this.share());
      }

      // Mobile swipe support
      this.setupSwipe();

      if (this.mobileCarousel) {
        this.mobileCarousel.addEventListener('scroll', () =>
          this.updateMobileProgress()
        );
      }

      window.addEventListener('resize', () => this.updateMobileProgress());
    }

    goToSlide(index) {
      if (index < 0 || index >= this.slides.length) return;

      this.pauseInactiveMedia(index);

      // Deactivate all
      this.slides.forEach((slide) =>
        slide.classList.remove('smart-gallery__slide--active')
      );
      this.thumbs.forEach((thumb) =>
        thumb.classList.remove('smart-gallery__thumb--active')
      );

      // Activate target
      this.slides[index].classList.add('smart-gallery__slide--active');

      // Activate corresponding thumbnails (both desktop and mobile)
      this.thumbs.forEach((thumb) => {
        if (parseInt(thumb.dataset.index, 10) === index) {
          thumb.classList.add('smart-gallery__thumb--active');
        }
      });

      this.currentIndex = index;

      // Scroll mobile carousel to show active thumb
      this.scrollMobileThumbIntoView(index);
      this.updateMobileProgress();
    }

    pauseInactiveMedia(activeIndex) {
      this.slides.forEach((slide, index) => {
        if (index === activeIndex) return;

        slide.querySelectorAll('video').forEach((video) => {
          video.pause();
        });

        // External video embeds do not expose a universal pause API, so resetting
        // the iframe source is the most reliable way to stop playback on slide change.
        slide.querySelectorAll('iframe').forEach((iframe) => {
          const src = iframe.dataset.smartSrc || iframe.getAttribute('src');
          if (!src) return;

          iframe.dataset.smartSrc = src;
          iframe.setAttribute('src', src);
        });
      });
    }

    scrollMobileThumbIntoView(index) {
      const mobileCarousel = this.gallery.querySelector(
        '.smart-gallery__thumbnails-carousel'
      );
      if (!mobileCarousel) return;

      const mobileThumb = mobileCarousel.children[index];
      if (mobileThumb) {
        mobileThumb.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }

    updateMobileProgress() {
      if (!this.mobileCarousel || !this.mobileProgress) return;

      const scrollWidth = this.mobileCarousel.scrollWidth;
      const clientWidth = this.mobileCarousel.clientWidth;
      if (scrollWidth <= clientWidth) {
        this.mobileProgress.style.width = '100%';
        this.mobileProgress.style.transform = 'translateX(0)';
        return;
      }

      const trackWidth = this.mobileProgress.parentElement.clientWidth;
      const thumbWidth = Math.max(
        34,
        Math.round((clientWidth / scrollWidth) * trackWidth)
      );
      const maxScroll = scrollWidth - clientWidth;
      const maxTranslate = trackWidth - thumbWidth;
      const translate =
        maxScroll > 0
          ? Math.round((this.mobileCarousel.scrollLeft / maxScroll) * maxTranslate)
          : 0;

      this.mobileProgress.style.width = `${thumbWidth}px`;
      this.mobileProgress.style.transform = `translateX(${translate}px)`;
    }

    setupSwipe() {
      const mainSlider = this.gallery.querySelector('[data-smart-main-slider]');
      if (!mainSlider) return;

      let startX = 0;
      let endX = 0;

      mainSlider.addEventListener(
        'touchstart',
        (e) => {
          startX = e.touches[0].clientX;
        },
        { passive: true }
      );

      mainSlider.addEventListener(
        'touchend',
        (e) => {
          endX = e.changedTouches[0].clientX;
          const diff = startX - endX;

          if (Math.abs(diff) > 50) {
            if (diff > 0) {
              // Swipe left - next
              this.goToSlide(
                Math.min(this.currentIndex + 1, this.slides.length - 1)
              );
            } else {
              // Swipe right - prev
              this.goToSlide(Math.max(this.currentIndex - 1, 0));
            }
          }
        },
        { passive: true }
      );
    }

    async share() {
      const url = window.location.href;
      const title = document.title;

      if (navigator.share) {
        try {
          await navigator.share({ title, url });
        } catch (e) {
          // User cancelled or error
        }
      } else {
        // Fallback: copy to clipboard
        try {
          await navigator.clipboard.writeText(url);
          // Could show a tooltip here
        } catch (e) {
          // Fallback for older browsers
          const input = document.createElement('input');
          input.value = url;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          document.body.removeChild(input);
        }
      }
    }
  }

  // Initialize
  const initSmartMediaGalleries = () => {
    document
      .querySelectorAll('[data-smart-gallery]')
      .forEach((gallery) => new SmartMediaGallery(gallery));
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initSmartMediaGalleries
    );
  } else {
    initSmartMediaGalleries();
  }
})();
